// TMOS User Position Service
// Date: December 19, 2025
// Purpose: Frontend service for user position management

import api from './api';
import { UserPosition } from './businessPositionService';

export interface UserPositionAssignment {
  userId: string;
  positionIds: string[];
  primaryPositionId?: string;
  hierarchyLocation?: {
    geo_id?: string;
    func_id?: string;
    bus_id?: string;
  };
}

export interface UserWithPositions {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  positions: UserPosition[];
  primaryPosition?: UserPosition;
}

export const userPositionService = {
  // Get positions for a specific user
  async getUserPositions(userId: string): Promise<UserPosition[]> {
    const response = await api.get(`/business-positions/user/positions`, {
      params: { userId }
    });
    return response.data.positions || [];
  },

  // Get all users with their positions (for tenant admin)
  async getUsersWithPositions(tenantId?: string): Promise<UserWithPositions[]> {
    const params = tenantId ? { tenantId } : {};
    const response = await api.get('/users/admin/users', { params });
    const users = response.data.data?.users || response.data.data || [];
    
    // Fetch positions for each user
    const usersWithPositions = await Promise.all(
      users.map(async (user: any) => {
        try {
          const positions = await this.getUserPositions(user.id);
          const primaryPosition = positions.find(p => p.is_primary);
          return {
            id: user.id,
            email: user.email || user.email_address,
            first_name: user.first_name || user.firstName,
            last_name: user.last_name || user.lastName,
            positions,
            primaryPosition
          };
        } catch (error) {
          console.error(`Error loading positions for user ${user.id}:`, error);
          return {
            id: user.id,
            email: user.email || user.email_address,
            first_name: user.first_name || user.firstName,
            last_name: user.last_name || user.lastName,
            positions: [],
            primaryPosition: undefined
          };
        }
      })
    );
    
    return usersWithPositions;
  },

  // Assign positions to user (targetUserId optional: for admin editing another user)
  async assignPositionsToUser(
    userId: string,
    assignment: UserPositionAssignment
  ): Promise<{ userPositions: UserPosition[]; assignedRoles: string[] }> {
    const results = [];
    const assignedRoles: string[] = [];
    const baseBody: { hierarchyLocation?: typeof assignment.hierarchyLocation; targetUserId?: string } = {
      ...(assignment.hierarchyLocation && { hierarchyLocation: assignment.hierarchyLocation }),
      ...(userId && { targetUserId: userId })
    };

    // Assign each position
    for (const positionId of assignment.positionIds) {
      try {
        const result = await api.post('/business-positions/user/assign-position', {
          ...baseBody,
          positionId
        });
        results.push(result.data.userPosition);
        if (result.data.assignedRole) {
          assignedRoles.push(result.data.assignedRole);
        }
      } catch (error: any) {
        console.error(`Error assigning position ${positionId}:`, error);
        // Continue with other positions even if one fails
      }
    }

    // Set primary position if specified
    if (assignment.primaryPositionId) {
      try {
        await api.post('/business-positions/user/set-primary-position', {
          positionId: assignment.primaryPositionId,
          ...(userId && { targetUserId: userId })
        });
      } catch (error: any) {
        console.error('Error setting primary position:', error);
      }
    }

    return {
      userPositions: results,
      assignedRoles: [...new Set(assignedRoles)] // Remove duplicates
    };
  },

  // Remove position from user
  async removePositionFromUser(
    userId: string,
    positionId: string
  ): Promise<void> {
    await api.delete(`/business-positions/user/positions/${positionId}`, {
      data: { targetUserId: userId }
    });
  },

  // Set primary position (optional targetUserId for admin editing another user)
  async setPrimaryPosition(positionId: string, targetUserId?: string): Promise<UserPosition> {
    const response = await api.post('/business-positions/user/set-primary-position', {
      positionId,
      ...(targetUserId && { targetUserId })
    });
    return response.data.position;
  },

  // Get users by position (filter)
  async getUsersByPosition(positionId: string, tenantId?: string): Promise<UserWithPositions[]> {
    const allUsers = await this.getUsersWithPositions(tenantId);
    return allUsers.filter(user => 
      user.positions.some(p => p.position_id === positionId)
    );
  }
};

